import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Empty, List, Modal, Tag } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { getPageLayouts } from '../../services/cardLayouts';
import { normalizeId } from '../../utils/ids';

/**
 * "Print…": which of this card's page layouts, for this record.
 *
 * Every tier the caller can see is offered — a card layout and a collection's
 * own and their own private one are all just sheets to print, and which tier
 * a sheet came from is a fact about who maintains it, shown as a tag rather
 * than used to hide anything.
 */

const SCOPE_LABEL = { card: 'Card', collection: 'Collection', user: 'Mine' };

export default function PrintLayoutPicker({ open, onClose, cardId, collectionId, itemId }) {
	const navigate = useNavigate();
	const [layouts, setLayouts] = useState(null);
	const [error, setError] = useState(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		if (!open || !cardId) return undefined;
		let active = true;
		setLayouts(null);
		setError(null);
		getPageLayouts(cardId, { collectionId })
			.then(rows => { if (active) { setLayouts(rows); setError(null); } })
			.catch(e => { if (active) setError(e); });
		return () => { active = false; };
	}, [open, cardId, collectionId, attempt]);

	const go = (layout) => {
		const id = normalizeId(layout._id);
		const query = itemId ? `?item=${normalizeId(itemId)}` : '';
		navigate(`/collections/${normalizeId(collectionId)}/print/${normalizeId(cardId)}/${id}${query}`);
		onClose?.();
	};

	const create = () => {
		navigate(`/collections/${normalizeId(collectionId)}/cards/${normalizeId(cardId)}/layouts/new`);
		onClose?.();
	};

	return (
		<Modal open={open} onCancel={onClose} title="Print" footer={null} width={520}>
			<DialogLoadState
				loaded={layouts !== null}
				error={Boolean(error)}
				isEmpty={layouts !== null && !layouts.length}
				empty={<Empty description="No page layouts for this card yet." />}
				label="this card's layouts"
				onRetry={() => setAttempt(n => n + 1)}
				skeleton={<Skeleton variant="list-row" count={3} />}
				minHeight={160}
			>
				<List
					bordered
					dataSource={layouts || []}
					renderItem={layout => (
						<List.Item
							actions={[<Button key="print" type="link" onClick={() => go(layout)}>Print</Button>]}
						>
							<List.Item.Meta
								title={layout.name}
								description={`${layout.body?.page?.size || 'letter'} · ${layout.body?.subject === 'query' ? 'a query' : 'one record'}${layout.body?.form ? ' · on a form' : ''}`}
							/>
							<Tag>{SCOPE_LABEL[layout.scope] || layout.scope}</Tag>
						</List.Item>
					)}
				/>
			</DialogLoadState>
			<div className="print-picker__foot">
				<Button onClick={create}>New layout…</Button>
			</div>
		</Modal>
	);
}
